import React, { useMemo, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { formatAssignmentDateSpan } from '../../../utils/dateUtils';
import { getConflictMetaData } from '../../../utils/leaderUtils';
import StatusBadge from '../../shared/StatusBadge/StatusBadge';
import './AssignmentHoverCard.css';
import './ConflictHoverCard.css';

/**
 * AssignmentHoverCard
 *
 * @param {Object} props Component props
 * @param {Object|null} props.assignment Assignment to display
 * @param {Object|null} props.position Screen position for the card
 * @param {Object|null} props.conflicts Conflict details for the assignment
 * @param {Array} props.workers Worker lookup list
 * @param {Array} props.vehicles Vehicle lookup list
 * @param {boolean} props.isConflictDetectionEnabled Whether to show conflicts
 * @param {Function} props.onMouseEnter Keeps the card open while hovering it
 * @param {Function} props.onMouseLeave Closes the card when leaving it
 */
export default function AssignmentHoverCard({ 
    assignment, 
    position, 
    conflicts = null, 
    workers = [], 
    vehicles = [], 
    kolonnen = [],
    isConflictDetectionEnabled = true,
    onMouseEnter, 
    onMouseLeave,
    onAssignmentClick
}) {
    const cardRef = useRef(null);
    const panelRef = useRef(null);
    const [measuredHeight, setMeasuredHeight] = useState(null);
    const [measuredSignature, setMeasuredSignature] = useState(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isAtEnd, setIsAtEnd] = useState(true);

    const dateLabel = assignment ? formatAssignmentDateSpan(assignment) : '';
    const workerCount = assignment?.workers?.length || 0;
    const vehicleCount = assignment?.vehicles?.length || 0;
    const workerLookup = new Map((workers || []).map(w => [String(w.id), w]));
    const vehicleLookup = new Map((vehicles || []).map(v => [String(v.id), v]));

    // Transform legacy conflicts into the modern "blocks" structure
    const conflictBlocks = useMemo(() => {
        if (!isConflictDetectionEnabled || !conflicts || conflicts.total === 0) return [];
        
        const kolonneLookup = new Map((kolonnen || []).map(k => [String(k.id), k]));
        const workerEntries = conflicts.workers || {};
        const vehicleEntries = conflicts.vehicles || {};
        
        const blocks = [];

        // Helper to format/translate messages
        const cleanConflictMsg = (msg) => {
            if (!msg) return "";
            // Translate status technical names to human readable German
            return msg.replace('Status: red', 'Status: gesperrt');
        };

        const getConflictMeta = getConflictMetaData;

        // 1. Group by Kolonne (Shared conflicts)
        const assignmentWorkers = assignment.workers || [];
        const assignmentVehicles = assignment.vehicles || [];
        
        const kGroups = {}; // kolonneId -> { workerIds: Set, vehicleIds: Set }
        
        assignmentWorkers.forEach(aw => {
            if (aw.kolonne_id) {
                const kid = String(aw.kolonne_id);
                if (!kGroups[kid]) kGroups[kid] = { workerIds: new Set(), vehicleIds: new Set() };
                kGroups[kid].workerIds.add(String(aw.worker_id));
            }
        });
        assignmentVehicles.forEach(av => {
            if (av.kolonne_id) {
                const kid = String(av.kolonne_id);
                if (!kGroups[kid]) kGroups[kid] = { workerIds: new Set(), vehicleIds: new Set() };
                kGroups[kid].vehicleIds.add(String(av.vehicle_id));
            }
        });

        const handledMessages = new Map(); // resourceId -> Set(messages handled as kolonne)

        Object.entries(kGroups).forEach(([kid, members]) => {
            const kolonne = kolonneLookup.get(kid);
            if (!kolonne) return;

            const allMemberIds = [...members.workerIds, ...members.vehicleIds];
            if (allMemberIds.length === 0) return;

            // Find common messages across ALL members of the kolonne that are IN the assignment
            const firstId = allMemberIds[0];
            const isFirstWorker = members.workerIds.has(firstId);
            const firstIdConfs = (isFirstWorker ? workerEntries[firstId] : vehicleEntries[firstId]) || [];

            const commonMsgs = firstIdConfs.filter(msg => {
                // Only "Doppelbelegung" conflicts are typically shared at team level
                if (!msg.includes('Doppelbelegung')) return false;
                return allMemberIds.every(id => {
                    const isW = members.workerIds.has(id);
                    const c = (isW ? workerEntries[id] : vehicleEntries[id]) || [];
                    return c.includes(msg);
                });
            });

            if (commonMsgs.length > 0) {
                const totalAffectedResources = allMemberIds.length;
                blocks.push({
                    type: 1, // Kolonne
                    reason: `Kolonne: ${kolonne.name || kolonne.code || 'Unbenannt'} (${totalAffectedResources} Ressourcen)`,
                    items: commonMsgs.map(cleanConflictMsg),
                    meta: getConflictMeta(commonMsgs[0] || 'Mehrfachbelegung')
                });
                
                // Track handled messages to avoid duplicates in individual lists
                allMemberIds.forEach(id => {
                    if (!handledMessages.has(id)) handledMessages.set(id, new Set());
                    commonMsgs.forEach(m => handledMessages.get(id).add(m));
                });
            }
        });

        // 2. Individual Workers (Person)
        Object.entries(workerEntries).forEach(([workerId, messages]) => {
            const filtered = (messages || []).filter(m => !handledMessages.get(workerId)?.has(m));
            if (filtered.length > 0) {
                const w = workerLookup.get(String(workerId));
                blocks.push({
                    type: 2, // Person
                    reason: `Person: ${w?.name || `Mitarbeiter ${workerId}`}`,
                    items: filtered.map(cleanConflictMsg),
                    meta: getConflictMeta(filtered[0] || 'Mehrfachbelegung')
                });
            }
        });

        // 3. Individual Vehicles (Fahrzeug)
        Object.entries(vehicleEntries).forEach(([vehicleId, messages]) => {
            const filtered = (messages || []).filter(m => !handledMessages.get(vehicleId)?.has(m));
            if (filtered.length > 0) {
                const v = vehicleLookup.get(String(vehicleId));
                blocks.push({
                    type: 3, // Fahrzeug
                    reason: `Fahrzeug: ${v?.license_plate || v?.internal_name || `Fahrzeug ${vehicleId}`}`,
                    items: filtered.map(cleanConflictMsg),
                    meta: getConflictMeta(filtered[0] || 'Mehrfachbelegung')
                });
            }
        });

        // Sort: Type order (Kolonne < Person < Fahrzeug) then name
        return blocks.sort((a, b) => {
            if (a.type !== b.type) return a.type - b.type;
            return a.reason.localeCompare(b.reason);
        });
    }, [conflicts, isConflictDetectionEnabled, workerLookup, vehicleLookup, kolonnen, assignment]);

    const currentSignature = useMemo(() => {
        const blockSig = conflictBlocks.map(b => `${b.reason}::${(b.items || []).join('|')}`).join('||');
        return `${position?.height || 0}:${position?.maxHeight || 0}:${assignment?.id}:${blockSig}`;
    }, [conflictBlocks, position?.height, position?.maxHeight, assignment?.id]);

    const measureMode = measuredSignature !== currentSignature;

    useLayoutEffect(() => {
        const card = cardRef.current;
        if (!card || !measureMode) return;
        const nextHeight = Math.ceil(card.getBoundingClientRect().height);
        if (nextHeight > 0) {
            setMeasuredHeight(nextHeight);
            setMeasuredSignature(currentSignature);
        }
    }, [currentSignature, measureMode]);

    useEffect(() => {
        const panel = panelRef.current;
        if (!panel) return;

        const syncScrollState = () => {
            const scrollable = panel.scrollHeight > panel.clientHeight + 1;
            const atEnd = !scrollable || panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 2;
            setIsScrollable(scrollable);
            setIsAtEnd(atEnd);
        };

        syncScrollState();
        panel.addEventListener('scroll', syncScrollState, { passive: true });
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncScrollState) : null;
        resizeObserver?.observe(panel);

        return () => {
            panel.removeEventListener('scroll', syncScrollState);
            resizeObserver?.disconnect();
        };
    }, [currentSignature, measureMode, measuredHeight]);

    if (!assignment || !position) return null;

    const panelMaxHeight = Math.max((position?.maxHeight || 0) - 110, 180);
    const clampHeight = Math.max(0, position?.height || 0);
    const shouldClamp = measuredHeight !== null && clampHeight > 0 && measuredHeight > (clampHeight + 5);

    return (
        <div
            ref={cardRef}
            className={`assignment-hover-card premium-hover-card ${conflictBlocks.length > 0 ? 'has-conflicts' : ''}`}
            data-measure-mode={measureMode ? 'true' : 'false'}
            style={{ 
                top: position.align === 'bottom' ? 'auto' : position.top,
                bottom: position.align === 'bottom' ? (window.innerHeight - position.top) : 'auto',
                left: position.left,
                height: measureMode ? 'auto' : (shouldClamp ? position.height : 'auto'),
                maxHeight: position.maxHeight,
                cursor: onAssignmentClick ? 'pointer' : 'default',
                '--conflict-panel-max-height': `${panelMaxHeight}px`
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => {
                e.stopPropagation();
                onAssignmentClick?.(assignment);
            }}
        >
            <div className="assignment-hover-card__header">
                <div className="assignment-hover-card__title">{assignment.project_name || 'Ohne Titel'}</div>
                <div className="assignment-hover-card__meta">{dateLabel}</div>
                
                <div className="assignment-hover-card__details">
                    {assignment.location && <div className="assignment-hover-card__row"><span>Ort</span><strong>{assignment.location}</strong></div>}
                    {assignment.status && (
                        <div className="assignment-hover-card__row">
                            <span>Status</span>
                            <div className="status-badge-container">
                                <StatusBadge status={assignment.status} />
                            </div>
                        </div>
                    )}
                </div>

                <div className="assignment-hover-card__chips">
                    <span className="chip-worker">👤 {workerCount} Mitarbeiter</span>
                    <span className="chip-vehicle">🚚 {vehicleCount} Fahrzeuge</span>
                    {assignment.all_day && assignment.start_datetime && <span className="chip-allday">📅 Ganztag</span>}
                    {assignment.notes && <span className="chip-notes" title={assignment.notes}>📝 Notiz</span>}
                </div>
            </div>

            {conflictBlocks.length > 0 && (
                <div className="assignment-hover-card__conflicts-wrapper" style={{ 
                    position: 'relative', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1, 
                    minHeight: 0,
                    overflow: 'hidden',
                    paddingBottom: isScrollable && !isAtEnd ? '40px' : '0px'
                }}>
                    <div className="conflict-hover-card__panel" ref={panelRef} style={{ maxHeight: 'var(--conflict-panel-max-height)', overflowY: 'auto' }}>
                        <div className="assignment-hover-card__conflicts-header">
                            Konflikte <span>{conflictBlocks.length}</span>
                        </div>
                        <div className="conflict-hover-card__sections">
                            {conflictBlocks.map((block, idx) => (
                                <div 
                                    key={`${block.reason}-${idx}`} 
                                    className={`conflict-entry conflict-entry--${block.meta.type}`}
                                >
                                    <div className="conflict-entry__header">
                                        <span className="conflict-entry__icon">{block.meta.icon}</span>
                                        <span className="conflict-entry__title">{block.reason}</span>
                                    </div>
                                    <div className="conflict-entry__items">
                                        {(block.items || []).map((item, i) => (
                                            <div key={i} className="conflict-entry__item">{item}</div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {isScrollable && !isAtEnd && (
                        <div className="conflict-hover-card__scroll-hint" style={{ bottom: '12px', zIndex: 10 }}>Weiter scrollen</div>
                    )}
                </div>
            )}
        </div>
    );
}
